import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

interface DialogProps {
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  onSubmit?: () => void;
  submitText?: string;
  disabled?: boolean;
  onCancel?: () => void;
  cancelText?: string;
  isLoading?: boolean;
  closeOnOutsideClick?: boolean;
}

const DialogComponent: React.FC<DialogProps> = ({
  open,
  isOpen,
  onOpenChange,
  onClose,
  title,
  description,
  children,
  footer,
  maxWidth = 'md',
  onSubmit,
  submitText = 'Submit',
  disabled = false,
  onCancel,
  cancelText = 'Cancel',
  isLoading = false,
  closeOnOutsideClick = true,
}: DialogProps) => {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  };

  const dialogOpen = open !== undefined ? open : isOpen;

  React.useEffect(() => {
    if (!dialogOpen) {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    };
  }, [dialogOpen]);

  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
    if (!newOpen && onClose) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleOpenChange(false);
  };

  return (
    <Dialog open={dialogOpen || false} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`${maxWidthClasses[maxWidth]} max-h-[90vh]`}
        closeOnOutsideClick={closeOnOutsideClick}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-4 px-4 min-h-0">
          {children}
        </div>

        {footer !== undefined ? (
          footer ? (
            <DialogFooter className="mt-4">{footer}</DialogFooter>
          ) : null
        ) : (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            {onSubmit && (
              <Button
                type="button"
                onClick={onSubmit}
                disabled={disabled || isLoading}
              >
                {isLoading ? 'Loading...' : submitText}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DialogComponent;
